import React, { useState, useEffect } from 'react';
import { Timer, Users, ArrowRight, X, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { SESSION_STATUS, SESSION_TYPE } from '../constants';
import { countActiveParticipants } from '../utils/participantUtils';
import Badge from './ui/Badge.jsx';

const ActiveSessionBanner = ({ activeSession, onDismiss }) => {
  const navigate = useNavigate();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [countdownTime, setCountdownTime] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [previousStatus, setPreviousStatus] = useState(activeSession?.status);

  // Nur echte Solo-Focus-Sessions (PRIVATE + isFocusMode) zur FocusSessionPage;
  // 1:1-PRIVATE geht zur SessionDetailPage (Partner-Validierung).
  const isFocusSession = activeSession?.type === SESSION_TYPE.PRIVATE && activeSession?.isFocusMode === true;

  // Auto-navigate when session becomes ACTIVE
  useEffect(() => {
    if (!activeSession) return;

    const currentStatus = activeSession.status;
    const isFocus = activeSession.type === SESSION_TYPE.PRIVATE && activeSession.isFocusMode === true;

    // Detect PLANNED → ACTIVE transition
    if (previousStatus === SESSION_STATUS.PLANNED && currentStatus === SESSION_STATUS.ACTIVE) {
      console.log('🚀 Session wurde aktiviert - Auto-Navigation zur Session...');
      navigate(isFocus ? `/focus-session/${activeSession.id}` : `/session/${activeSession.id}`);
    }

    setPreviousStatus(currentStatus);
  }, [activeSession?.status, activeSession?.type, activeSession?.isFocusMode, previousStatus, activeSession?.id, navigate]);

  useEffect(() => {
    if (!activeSession) {
      return;
    }

    // Timer for ACTIVE sessions (elapsed time)
    if (activeSession.status === SESSION_STATUS.ACTIVE) {
      const timer = setInterval(() => {
        const startTime = new Date(activeSession.startTime);
        const now = new Date();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);

      return () => clearInterval(timer);
    }

    // Countdown for PLANNED sessions (time until start)
    if (activeSession.status === SESSION_STATUS.PLANNED && activeSession.startTime) {
      const timer = setInterval(() => {
        const startTime = new Date(activeSession.startTime);
        const now = new Date();
        const remaining = Math.floor((startTime - now) / 1000);
        setCountdownTime(Math.max(0, remaining)); // Never negative
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [activeSession]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    // Wie im Spot „00:38“ — mit fuehrender Null, damit die Breite stehen bleibt.
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNavigateToSession = () => {
    if (activeSession?.id) {
      navigate(isFocusSession ? `/focus-session/${activeSession.id}` : `/session/${activeSession.id}`);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  // Don't render if no session or dismissed
  const isActive = activeSession?.status === SESSION_STATUS.ACTIVE;
  const isPlanned = activeSession?.status === SESSION_STATUS.PLANNED;

  // PLANNED Sessions: nur anzeigen wenn < 5 Minuten bis Start
  const isPlannedSoon = isPlanned && activeSession.startTime &&
    (new Date(activeSession.startTime) - new Date()) < 5 * 60 * 1000;

  if (!activeSession || (!isActive && !isPlannedSoon) || isDismissed) {
    return null;
  }

  const participantCount = countActiveParticipants(activeSession.participants) || 1;
  const maxParticipants = activeSession.maxParticipants || 2;

  // "Solo" nur bei echten Einzel-Sessions (maxParticipants=1) anzeigen, NICHT wenn
  // bei einer Mehrpersonen-Session der Group-Focus-Modus eingeschaltet ist (isFocusMode).
  const isSoloSession = maxParticipants <= 1 && participantCount <= 1;

  // Fortschritt der ersten 15 Minuten (aktiv) bzw. der letzten 15 bis zum Start.
  const progress = isActive
    ? Math.min(elapsedTime / 900, 1)
    : Math.max(1 - countdownTime / 900, 0);

  return (
    // Oktober 2026 („App wie Spot“): eine schwebende Karte statt einer Leiste ueber
    // die volle Breite — helle Lila-Flaeche, weisse Icon-Kachel mit ruhigem Punkt
    // (vorher pulsierte das Timer-Icon und der Punkt pingte endlos), Zeit in Tinte,
    // darunter „Aktive Session: Thema“ auch auf dem Telefon (vorher ausgeblendet),
    // unten eine 2-px-Linie fuer den Fortschritt.
    // pointer-events: Der Rand um die Karte ist durchsichtig und darf keine Klicks
    // auf den Seitenkopf darunter abfangen.
    <div className="pointer-events-none px-3 pt-2 animate-slide-down">
      <div className="pointer-events-auto relative mx-auto flex max-w-xl items-center gap-3 overflow-hidden rounded-2xl border border-primary/20 bg-primary-soft px-3.5 py-3 text-foreground shadow-token-md">
        {/* Die ganze Karte fuehrt zur Session */}
        <button
          type="button"
          onClick={handleNavigateToSession}
          aria-label="Zur Session"
          className="absolute inset-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        />

        <span className="pointer-events-none relative flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-xl bg-paper text-success" aria-hidden="true">
          <Timer className="h-5 w-5" />
          <span className={`absolute -right-[3px] -top-[3px] h-2.5 w-2.5 rounded-full border-2 border-paper ${isActive ? 'bg-success' : 'bg-warning'}`} />
        </span>

        <div className="pointer-events-none relative min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="font-mono text-[17px] font-semibold tabular-nums text-foreground">
              {isActive ? formatTime(elapsedTime) : formatTime(countdownTime)}
            </span>
            {isPlanned && <span className="text-meta text-muted-foreground">bis Start</span>}
          </div>
          <div className="truncate text-meta text-muted-foreground">
            <span>{isActive ? 'Aktive Session:' : 'Geplante Session:'}</span>{' '}
            <b className="font-semibold text-foreground">{activeSession.topic || 'Lernsession'}</b>
          </div>
        </div>

        <div className="pointer-events-none relative flex shrink-0 items-center gap-2">
          <Badge size="lg" icon={isSoloSession ? User : Users}>
            {isSoloSession ? 'Solo' : `${participantCount}/${maxParticipants}`}
          </Badge>
          <ArrowRight className="h-4 w-4 text-foreground" aria-hidden="true" />
        </div>

        {/* Schliessen nur fuer geplante Sessions (aktive bleiben stehen) */}
        {onDismiss && (
          <button
            type="button"
            onClick={handleDismiss}
            className="relative -my-1 -mr-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Banner schließen"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        <div
          className="absolute bottom-0 left-0 h-[2px] w-full origin-left bg-primary transition-transform duration-1000 ease-linear"
          style={{ transform: `scaleX(${progress})` }}
          data-testid="session-progress"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

ActiveSessionBanner.propTypes = {
  activeSession: PropTypes.shape({
    id: PropTypes.string,
    topic: PropTypes.string,
    status: PropTypes.string,
    startTime: PropTypes.string,
    type: PropTypes.string,
    isFocusMode: PropTypes.bool,
    participants: PropTypes.array,
    maxParticipants: PropTypes.number
  }),
  onDismiss: PropTypes.func
};

export default ActiveSessionBanner;